import { useRef, useState } from "react";
import { AlertCircle, AlertTriangle, FunctionSquare, ListTree } from "lucide-react";
import { cn } from "@/lib/utils";
import { FieldFormula, VariableOption } from "@/types/formula";
import { isMarked, isPlainPath, marked, sourceOf } from "@/utils/formula";
import { useFormulaCheck } from "@/hooks/use-formula-check";
import { useVariableCatalog } from "@/hooks/use-formula-catalog";
import { FormulaEditor } from "./formula-editor";
import { FormulaModal } from "./formula-modal";
import { VariableSuggestions } from "./variable-suggestions";

type FormulaFieldProps = {
    id: string;
    formula: FieldFormula;
    value: string;
    disabled?: boolean;
    /** A field the server declared large gets the room a message body needs. */
    large?: boolean;
    /** What the modal calls the field, and what it says about it. Both the server's words. */
    label?: string;
    description?: string;
    /**
     * One line, the environment a click away: what a row of a list of templates gets.
     * Only a template honours it — a path already is one line.
     */
    compact?: boolean;
    onChange: (value: string) => void;
};

/**
 * The one input every formula field in the CRM gets, and the choice of which one it is.
 *
 * **Most of these fields only ever hold a path.** « Which number do I dial », « where do I
 * read the sender's address » — a hundred of them across the handlers, and imposing an
 * expression editor with an operator row and a function palette on each would be a
 * regression: the author of a phone number does not want a language, they want the list of
 * fields. So the shape of the value decides, through the same reading `is_plain_path` makes
 * server-side: empty or a bare dotted path gets the picker, anything else already is an
 * expression and gets the editor. The reading is taken when the field is opened and then
 * left alone — a value being typed must not swap the input under the caret — and both ways
 * are one click.
 *
 * A template is never a choice: text with `{{ … }}` holes is not a path.
 *
 * **A switchable field is a third case, and it is not offered the choice at all.** It is
 * rendered here only while it holds an expression — its literal mode is the input of its
 * declared type, which is `DynamicFormField`'s business — so it gets one input: the source
 * box, compact, with the whole environment one click away. Offering the picker as well would
 * make three modes of a control whose one documented failure is the confusion between two.
 */
export function FormulaField({ id, formula, value, disabled, large, label, description, compact, onChange }: FormulaFieldProps) {
    const { options, loading } = useVariableCatalog(formula.catalog_url);
    const [expression, setExpression] = useState(() => formula.mode === "template" || (value.trim() !== "" && !isPlainPath(value)));
    const [expanded, setExpanded] = useState(false);

    if (formula.switchable) {
        // The mark is the field's, not the editor's: everything below this line — the
        // check, the marks, the suggestions, the modal — reads and writes a bare source.
        const source = isMarked(value) ? sourceOf(value) : value;
        const write = (next: string) => onChange(marked(next));
        return (
            <>
                <FormulaEditor
                    id={id}
                    value={source}
                    formula={formula}
                    options={options}
                    loadingOptions={loading}
                    disabled={disabled}
                    rows={large ? 4 : 2}
                    compact
                    onChange={write}
                    onExpand={disabled ? undefined : () => setExpanded(true)}
                />
                {expanded && (
                    <FormulaModal
                        id={id}
                        label={label || id}
                        description={description}
                        formula={formula}
                        value={source}
                        options={options}
                        loadingOptions={loading}
                        onChange={write}
                        onClose={() => setExpanded(false)}
                    />
                )}
            </>
        );
    }

    if (formula.mode === "template") {
        // A message body is the field most worth reading whole, so it gets the roomy
        // editor too; a template row of a list gets the one-line box and the same way out.
        return (
            <>
                <FormulaEditor
                    id={id}
                    value={value}
                    formula={formula}
                    options={options}
                    loadingOptions={loading}
                    disabled={disabled}
                    // One line to start with; a compact box grows with what it holds.
                    rows={compact ? 1 : large ? 8 : undefined}
                    compact={compact}
                    quiet={compact}
                    onChange={onChange}
                    onExpand={disabled ? undefined : () => setExpanded(true)}
                />
                {expanded && (
                    <FormulaModal
                        id={id}
                        label={label || id}
                        description={description}
                        formula={formula}
                        value={value}
                        options={options}
                        loadingOptions={loading}
                        onChange={onChange}
                        onClose={() => setExpanded(false)}
                    />
                )}
            </>
        );
    }

    if (expression) {
        return (
            <FormulaEditor
                id={id}
                value={value}
                formula={formula}
                options={options}
                loadingOptions={loading}
                disabled={disabled}
                rows={large ? 6 : undefined}
                onChange={onChange}
                onSimplify={!isPlainPath(value) ? undefined : () => setExpression(false)}
            />
        );
    }

    return <PathPicker id={id} formula={formula} value={value} options={options} loading={loading} disabled={disabled} onChange={onChange} onComplicate={() => setExpression(true)} />;
}

type PathPickerProps = {
    id: string;
    formula: FieldFormula;
    value: string;
    options: VariableOption[];
    loading?: boolean;
    disabled?: boolean;
    onChange: (value: string) => void;
    onComplicate: () => void;
};

/**
 * A field designated from the catalogue, and nothing more.
 *
 * Still checked by the engine, because a path is the degenerate case of a formula and
 * `snapshot.mobil_phone` is a typo the author should learn about now rather than at
 * publication. Not blocked, though: the drawer saves as it is typed and this input holds to
 * that — a wrong path is a sentence under the box, not a refused keystroke.
 */
function PathPicker({ id, formula, value, options, loading, disabled, onChange, onComplicate }: PathPickerProps) {
    const input = useRef<HTMLInputElement>(null);
    const [focused, setFocused] = useState(false);
    const [browsing, setBrowsing] = useState(false);
    const [dismissed, setDismissed] = useState<string | null>(null);

    const check = useFormulaCheck(value, formula);
    const issues = check.checked === value ? (check.result?.issues ?? []) : [];
    const worst = issues.find(issue => issue.severity === "error") ?? issues[0];
    const selected = options.find(option => option.value === value);
    const wandered = value.trim() !== "" && !isPlainPath(value);
    const suggesting = focused && !disabled && dismissed !== value && (browsing || value.length > 0);

    return (
        <div className="flex flex-col gap-1" data-testid={`path-picker-${id}`}>
            <div className="relative flex items-center gap-1">
                <input
                    ref={input}
                    id={id}
                    name={id}
                    value={value}
                    disabled={disabled}
                    spellCheck={false}
                    aria-invalid={Boolean(worst && worst.severity === "error")}
                    placeholder="Pick a field, e.g. snapshot.mobile_phone"
                    className={cn("h-9 w-full rounded border border-input bg-transparent px-2 py-1 font-mono text-[12px]", worst?.severity === "error" && "border-red-400")}
                    onChange={event => {
                        setDismissed(null);
                        onChange(event.target.value);
                    }}
                    onFocus={() => setFocused(true)}
                    onBlur={() => {
                        setFocused(false);
                        setBrowsing(false);
                    }}
                    onKeyDown={event => {
                        if (event.key === "Escape" && suggesting) {
                            event.preventDefault();
                            setDismissed(value);
                            setBrowsing(false);
                        }
                    }}
                />
                {!disabled && (
                    <button
                        type="button"
                        aria-label="Browse the variables"
                        data-testid={`path-browse-${id}`}
                        onMouseDown={event => {
                            event.preventDefault();
                            input.current?.focus();
                            setDismissed(null);
                            setBrowsing(true);
                        }}
                        className="flex h-9 shrink-0 items-center rounded border px-1.5 text-gray-600 hover:bg-gray-50"
                    >
                        <ListTree className="h-3.5 w-3.5" />
                    </button>
                )}
                {suggesting && (
                    <VariableSuggestions
                        options={options}
                        query={value}
                        loading={loading}
                        onPick={(path, continues) => {
                            // A way in keeps the list open on the scope it just entered; a
                            // value closes it, and `dismissed` is what stops the list from
                            // reopening on the path it has just written.
                            const next = continues ? `${path}.` : path;
                            onChange(next);
                            setDismissed(continues ? null : next);
                            setBrowsing(continues);
                        }}
                        className="absolute left-0 right-0 top-full z-30 mt-1"
                    />
                )}
            </div>

            {wandered ? (
                <button
                    type="button"
                    onClick={onComplicate}
                    data-testid={`path-complicate-${id}`}
                    className="flex items-start gap-1 self-start text-left text-[11px] text-sky-700 underline hover:text-sky-900"
                >
                    <FunctionSquare className="mt-0.5 h-3 w-3 shrink-0" />
                    This is no longer a single field. Open the expression editor to see what is wrong with it.
                </button>
            ) : worst ? (
                <p className={cn("flex items-start gap-1 text-[11px] leading-snug", worst.severity === "error" ? "text-red-700" : "text-amber-800")} data-testid={`path-issue-${id}`}>
                    {worst.severity === "error" ? <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" /> : <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />}
                    {worst.message}
                </p>
            ) : selected ? (
                <p className="text-[11px] leading-snug text-gray-500" data-testid={`path-reading-${id}`}>
                    <span className="uppercase">{selected.type}</span>
                    {selected.description ? ` · ${selected.description}` : selected.label ? ` · ${selected.label}` : ""}
                </p>
            ) : (
                <p className="text-[11px] text-gray-500">Reads a field of the card. This one wants {formula.expects === "any" ? "any value" : formula.expects}.</p>
            )}

            {!disabled && !wandered && (
                <button type="button" onClick={onComplicate} data-testid={`path-to-expression-${id}`} className="self-start text-[11px] text-sky-700 underline hover:text-sky-900">
                    Compute it instead of reading it
                </button>
            )}
        </div>
    );
}
